import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PageHeader from '@/components/feature/PageHeader';
import { articles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

const verifyPoints = [
  {
    icon: 'ri-user-search-line',
    title: 'Check the source',
    desc: 'Look at who is writing and whether they are transparent about not being affiliated with AllChinaBuy. Independent resources should say so clearly.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Verify current details',
    desc: 'Prices, sellers and shipping options change. Treat any quoted price as a snapshot, not a promise, and re-check it on the product page.',
  },
  {
    icon: 'ri-checkbox-circle-line',
    title: 'Look for checkable advice',
    desc: 'Useful research gives you concrete steps to follow, not just vague praise or criticism. Specific advice is easier to verify.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Cross-check claims',
    desc: 'Compare what you read against the product page, QC photos and your own observations before making a decision.',
  },
];

export default function Reviews() {
  const { t } = useTranslation();
  const relatedSlugs = ['is-allchinabuy-legit', 'allchinabuy-buying-guide', 'allchinabuy-coupon-guide'];
  const related = articles.filter((a) => relatedSlugs.includes(a.slug));

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <PageHeader
          titleKey="page.reviews.title"
          subtitleKey="page.reviews.subtitle"
          breadcrumbKey="nav.reviews"
        />

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-3xl space-y-5">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              Reviews and independent research matter more than ever when you buy through an agent-style service like AllChinaBuy. This page explains what genuinely useful research looks like, what this website does and does not claim, and how you can evaluate the platform honestly before you order.
            </p>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              This website is an independent informational resource. We do not fabricate reviews, ratings, credentials or partnerships, and we do not claim to be affiliated with AllChinaBuy.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {verifyPoints.map((p) => (
              <div
                key={p.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex w-11 h-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <h2 className="mt-4 font-heading text-base md:text-lg font-bold text-foreground-950">{p.title}</h2>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              Key factors buyers actually care about
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              In our research we focus on the areas that matter most: how the spreadsheet is organized, how QC photos help you spot issues, how shipping costs are calculated, and how to avoid common mistakes. Understanding these factors is more useful than a single star rating, because they give you a repeatable way to evaluate any order.
            </p>
          </div>

          <div className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Continue your research</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-all hover:border-primary-300 cursor-pointer"
                >
                  <div className="h-32 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      title={`${a.title} AllChinaBuy Spreadsheet`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-14 rounded-lg bg-primary-50 border border-primary-200 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm md:text-base text-foreground-800 leading-relaxed">
              Ready to browse the spreadsheet and compare categories for yourself?
            </p>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0 cursor-pointer"
            >
              {t('hero.ctaPrimary')}
              <span className="flex w-4 h-4 items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}